<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Sun, Moon, Monitor, Bell, RefreshCw, Menu } from 'lucide-vue-next'
import { useThemeStore, type ThemeMode } from '@/stores/theme'
import { setLocale, type AppLocale } from '@/locales'

const route = useRoute()
const { t, locale } = useI18n()
const theme = useThemeStore()

const emit = defineEmits<{ 'toggle-menu': [] }>()

// v3.13.82: 标题走 i18n titleKey (之前直接显示路由英文 meta)
const title = computed(() => {
  const key = route.meta.titleKey as string | undefined
  if (key) return t(key)
  return (route.meta.title as string | undefined) ?? t('common.appName')
})
const refreshing = ref(false)

const themeOptions: Array<{ value: ThemeMode; label: string; icon: typeof Sun }> = [
  { value: 'light', label: t('theme.light'), icon: Sun },
  { value: 'dark', label: t('theme.dark'), icon: Moon },
  { value: 'auto', label: t('theme.auto'), icon: Monitor },
]

function setTheme(mode: ThemeMode): void {
  theme.setMode(mode)
}

function switchLocale(loc: AppLocale): void {
  setLocale(loc)
  // touch locale to keep reactivity
  void locale.value
}

function refresh(): void {
  refreshing.value = true
  // Trigger window location reload as a baseline; views can override.
  window.setTimeout(() => {
    refreshing.value = false
  }, 600)
}
</script>

<template>
  <header
    class="glass-panel mx-2 mt-2 flex h-14 shrink-0 items-center justify-between rounded-2xl px-3 sm:mx-4 sm:mt-4 sm:px-5"
  >
    <div class="flex min-w-0 items-center gap-2 sm:gap-3">
      <!-- v3.13.81: 移动端汉堡按钮 (<md), 触发 MainLayout 抽屉 -->
      <button
        type="button"
        class="rounded-md p-2 text-fg-muted transition-colors duration-fast hover:bg-bg-hover hover:text-fg md:hidden"
        :aria-label="t('nav.menu')"
        @click="emit('toggle-menu')"
      >
        <Menu :size="18" />
      </button>
      <h1 class="min-w-0 truncate text-base font-semibold text-fg">{{ title }}</h1>
      <span v-if="route.meta.subtitle" class="hidden truncate text-sm text-fg-muted sm:inline">
        {{ route.meta.subtitle }}
      </span>
    </div>

    <div class="flex shrink-0 items-center gap-1 sm:gap-2">
      <!-- Refresh -->
      <button
        type="button"
        class="rounded-md p-2 text-fg-muted transition-colors duration-fast hover:bg-bg-hover hover:text-fg"
        :disabled="refreshing"
        :title="t('common.refresh')"
        @click="refresh"
      >
        <RefreshCw :size="16" :class="refreshing ? 'animate-spin' : ''" />
      </button>

      <!-- Theme switcher (桌面; 手机上主题切换在抽屉底部) -->
      <div class="hidden items-center rounded-md border border-border bg-bg-surface p-0.5 md:flex">
        <button
          v-for="opt in themeOptions"
          :key="opt.value"
          type="button"
          class="flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium transition-colors duration-fast"
          :class="
            theme.mode === opt.value
              ? 'bg-brand-500/15 text-brand-600 dark:text-brand-300'
              : 'text-fg-muted hover:text-fg'
          "
          :title="opt.label"
          @click="setTheme(opt.value)"
        >
          <component :is="opt.icon" :size="14" />
        </button>
      </div>

      <!-- Language (桌面) -->
      <div class="hidden items-center rounded-md border border-border bg-bg-surface p-0.5 md:flex">
        <button
          type="button"
          class="rounded px-2.5 py-1.5 text-xs font-medium transition-colors duration-fast"
          :class="
            locale === 'zh-CN'
              ? 'bg-brand-500/15 text-brand-600 dark:text-brand-300'
              : 'text-fg-muted hover:text-fg'
          "
          @click="switchLocale('zh-CN')"
        >
          中
        </button>
        <button
          type="button"
          class="rounded px-2.5 py-1.5 text-xs font-medium transition-colors duration-fast"
          :class="
            locale === 'en'
              ? 'bg-brand-500/15 text-brand-600 dark:text-brand-300'
              : 'text-fg-muted hover:text-fg'
          "
          @click="switchLocale('en')"
        >
          EN
        </button>
      </div>

      <!-- Notifications -->
      <button
        type="button"
        class="relative rounded-md p-2 text-fg-muted hover:bg-bg-hover hover:text-fg transition-colors duration-fast"
        :title="t('common.status')"
      >
        <Bell :size="16" />
      </button>
    </div>
  </header>
</template>
